import React from 'react'
import {Box} from '@mui/material'
import type {Area} from '../utils/colors'

// Regime jurídico da análise. Define a base legal usada pela IA (persona/prompt),
// a base de conhecimento (Pinecone) e o conjunto de tipos de documento exibidos.
// '14133' = Lei Geral de Licitações (Administração Pública).
// '13303' = Lei das Estatais (empresas públicas e sociedades de economia mista).
// '14903' = Marco Legal da Cultura (fomento cultural, área Cultura da SMC).
export type Regime = '14133' | '13303' | '14903'

export interface DocType {
    id: string
    apiId: string
    short: string
    name: string
    desc: string
    accent: string
    soft: string
    iconPath: string
    phase: string
    avgPages: string
    featured?: boolean
    // Ausente = '14133' (regime padrão). Tipos da Lei das Estatais marcam '13303'.
    regime?: Regime
    // LCTO-1920: área do switch Licitação/Cultura da SMC (LCTO-1915). Ausente =
    // 'licitacao'. Tipos de 'cultura' só aparecem com o switch em Cultura.
    area?: Area
    // Base de conhecimento (Pinecone) da análise. Ausente = a do regime.
    databaseSlug?: string
}

export const DOC_TYPES: DocType[] = [
    {
        id: 'edital',
        apiId: '3514c57a-6ed3-4472-b4d5-2f1d9815400f',
        short: 'EDITAL',
        name: 'Edital (com ou sem anexos)',
        desc: 'Recomendado: envie o edital consolidado com seus anexos (TR, minuta de contrato, planilhas) em um único arquivo.',
        accent: '#050B29',
        soft: 'rgba(5,11,41,0.07)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '40-150 págs',
        featured: true,
    },
    {
        id: 'tr',
        apiId: '8b34584f-7781-4c91-8bd2-2f79c3889a6e',
        short: 'TR',
        name: 'Termo de Referência',
        desc: 'Especificação técnica detalhada do objeto da contratação.',
        accent: '#3acdb4',
        soft: 'rgba(58,205,180,0.12)',
        iconPath: 'M4 4h16v4H4zM4 10h10v10H4zM16 10h4v4h-4zM16 16h4v4h-4z',
        phase: 'Planejamento',
        avgPages: '20-60 págs',
    },
    {
        id: 'etp',
        apiId: 'c8eb8b29-758e-4898-9f10-e10c7d35e10f',
        short: 'ETP',
        name: 'Estudo Técnico Preliminar',
        desc: 'Documento que materializa o estudo técnico que fundamenta a contratação.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '12-30 págs',
    },
    {
        id: 'contrato',
        apiId: 'b7799217-ba68-408f-931d-ab3b1f28b9aa',
        short: 'CONTRATO',
        name: 'Contrato Administrativo',
        desc: 'Instrumento que disciplina as obrigações entre Administração e contratado.',
        accent: '#B31417',
        soft: 'rgba(179,20,23,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '15-50 págs',
    },
    {
        id: 'mr',
        apiId: '2a2edaf0-c8c5-4b7a-8759-dcc487dc5921',
        short: 'MR',
        name: 'Mapa de Riscos',
        desc: 'Identificação, avaliação e tratamento de riscos da contratação.',
        accent: '#E0951B',
        soft: 'rgba(224,149,27,0.12)',
        iconPath: 'M12 3 2 21h20L12 3zM12 10v5M12 17.5h0.01',
        phase: 'Planejamento',
        avgPages: '5-15 págs',
    },
    {
        id: 'dfd',
        apiId: '3b86581f-cf66-40d1-96b3-950a9d709827',
        short: 'DFD',
        name: 'Documento de Formalização da Demanda',
        desc: 'Marco inicial do planejamento — registra a necessidade da contratação.',
        accent: '#7C5CFF',
        soft: 'rgba(124,92,255,0.10)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h4',
        phase: 'Planejamento',
        avgPages: '2-6 págs',
    },
    {
        id: 'ata',
        apiId: 'b7287010-9bfe-419f-bb0c-b768cb1ac739',
        short: 'ATA',
        name: 'Ata de Registro de Preços',
        desc: 'Documento vinculativo, obrigacional, com características de compromisso para futura contratação.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
        iconPath:
            'M8 2h8a2 2 0 0 1 2 2v18l-6-3-6 3V4a2 2 0 0 1 2-2zM10 8h4M10 12h4',
        phase: 'Contratação',
        avgPages: '8-25 págs',
    },
    // ───────────────────────────────────────────────────────────────────
    // Lei das Estatais (Lei nº 13.303/2016). Os `apiId` (document_type_id)
    // serão preenchidos quando as minutas-gabarito das estatais forem
    // cadastradas como static_metrics no backend. Enquanto o apiId estiver
    // vazio, o tipo fica oculto (filtrado por `visibleDocumentTypes`).
    // ───────────────────────────────────────────────────────────────────
    {
        id: 'edital-estatal',
        // document_type_id reservado para o Edital da Lei das Estatais. O tipo só
        // aparece depois que o static_metric com este id for cadastrado no backend
        // (ver docs/regime-lei-estatais.md). Até lá, fica oculto.
        apiId: '4667f060-4c7c-4310-b2c1-43ab3f1ffad8',
        short: 'EDITAL',
        name: 'Edital (com ou sem anexos)',
        desc: 'Recomendado: envie o edital consolidado com seus anexos (TR, minuta de contrato, planilhas) em um único arquivo.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '40-150 págs',
        featured: true,
        regime: '13303',
    },
    {
        id: 'tr-estatal',
        // document_type_id reservado para o TR da Lei das Estatais (métrica de
        // análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: 'c19736c7-4f83-45f5-baab-694818836774',
        short: 'TR',
        name: 'Termo de Referência',
        desc: 'Especificação técnica detalhada do objeto da contratação.',
        accent: '#3acdb4',
        soft: 'rgba(58,205,180,0.12)',
        iconPath: 'M4 4h16v4H4zM4 10h10v10H4zM16 10h4v4h-4zM16 16h4v4h-4z',
        phase: 'Planejamento',
        avgPages: '20-60 págs',
        regime: '13303',
    },
    {
        id: 'etp-estatal',
        // document_type_id reservado para o ETP da Lei das Estatais (métrica de
        // análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: 'c5b3be2b-6c1d-4baf-b741-29bc29377cbd',
        short: 'ETP',
        name: 'Estudo Técnico Preliminar',
        desc: 'Documento que materializa o estudo técnico que fundamenta a contratação.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '12-30 págs',
        regime: '13303',
    },
    {
        id: 'contrato-estatal',
        // document_type_id reservado para o Contrato da Lei das Estatais (métrica
        // de análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: '0aeb1f6b-b27d-4782-bde1-6c469ff8c0e6',
        short: 'CONTRATO',
        name: 'Contrato',
        desc: 'Instrumento que disciplina as obrigações entre a estatal e o contratado.',
        accent: '#B31417',
        soft: 'rgba(179,20,23,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '15-50 págs',
        regime: '13303',
    },
    // ───────────────────────────────────────────────────────────────────
    // LCTO-1920: fomento cultural da SMC-SP (Lei nº 14.903/2024). A API só
    // devolve estes ids para as orgs da SMC, e eles só aparecem na área
    // Cultura do switch. A análise usa a base de conhecimento de cultura.
    // ───────────────────────────────────────────────────────────────────
    {
        id: 'edital-chamamento',
        apiId: 'da983a0e-72d2-41c0-89b5-472387791424',
        short: 'CHAMAMENTO',
        name: 'Edital de Chamamento Público',
        desc: 'Edital que seleciona os projetos culturais que receberão o fomento.',
        accent: '#7B2CBF',
        soft: 'rgba(123,44,191,0.10)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '20-80 págs',
        area: 'cultura',
        databaseSlug: 'smcultura-2026-08-14',
    },
    {
        id: 'termo-execucao-cultural',
        apiId: 'de3b7b90-6145-47ab-8bee-04e32ea6ebd5',
        short: 'TERMO',
        name: 'Termo de Execução Cultural',
        desc: 'Instrumento que formaliza o fomento e as obrigações do agente cultural.',
        accent: '#C2185B',
        soft: 'rgba(194,24,91,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '5-20 págs',
        area: 'cultura',
        databaseSlug: 'smcultura-2026-08-14',
    },
    {
        id: 'estudo-planejamento-fomento',
        apiId: '1355121e-ae32-4d7c-8e96-c85ca72ab36c',
        short: 'ESTUDO',
        name: 'Estudo de Planejamento do Fomento',
        desc: 'Estudo que fundamenta o planejamento da ação de fomento cultural.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '10-30 págs',
        area: 'cultura',
        databaseSlug: 'smcultura-2026-08-14',
    },
]

export const findDocTypeByApiId = (apiId?: string | null): DocType | null =>
    (apiId && DOC_TYPES.find((docType) => docType.apiId === apiId)) || null

// LCTO-1933: nome exibido do tipo para a organização. `customNames` vem do GET
// /users do gerador (apiId -> nome); sem nome personalizado, vale o da lista.
export const getDocTypeName = (
    docType: Pick<DocType, 'apiId' | 'name'>,
    customNames?: Record<string, string> | null
): string => customNames?.[docType.apiId]?.trim() || docType.name

// Tipos exibidos no R.E.D.: os que a API devolve (métricas implementadas) e que
// são da área atual; se a API não devolver nada, os da 14.133 da área.
// LCTO-1933: o nome sai com o personalizado da organização.
export const getVisibleDocTypes = (
    availableIds: string[],
    area: Area = 'licitacao',
    customNames?: Record<string, string> | null
): DocType[] => {
    const available = new Set(availableIds)
    return DOC_TYPES.filter(
        (doc) =>
            Boolean(doc.apiId) &&
            (doc.area ?? 'licitacao') === area &&
            (available.size ? available.has(doc.apiId) : !doc.regime)
    ).map((doc) => ({...doc, name: getDocTypeName(doc, customNames)}))
}

type DocumentTypeBadgeProps = {
    docType: Pick<DocType, 'short' | 'accent' | 'soft'>
    iconPath?: string | null
    phase?: string | null
    sx?: Record<string, unknown>
}

export function DocumentTypeBadge({docType, iconPath, phase, sx}: DocumentTypeBadgeProps) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '999px',
                border: `1px solid ${docType.accent}`,
                backgroundColor: docType.soft,
                color: docType.accent,
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ...sx,
            }}
        >
            {iconPath && (
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d={iconPath} />
                </svg>
            )}
            {docType.short}
            {phase && <span style={{fontWeight: 400, opacity: 0.8}}>· {phase}</span>}
        </Box>
    )
}
